import { observable, action, reaction } from 'mobx';
import { GlobalAdministrationService } from '../service/GlobalAdministrationService';
import { LoadedMlModel, TargetMlModelService } from '../types';

// Time interval options for auto-refresh
export const timeOptions = [
    {
        value: 5000,
        label: '5 seconds'
    },
    {
        value: 10000,
        label: '10 seconds'
    },
    {
        value: 20000,
        label: '20 seconds'
    }
];

// Default refresh interval is 10 seconds
export const defaultIntervalTime = timeOptions[1].value;

export class MlModelsManagementStore {
    // Observable state for loaded models list
    @observable
    loadedModels: LoadedMlModel[] = [];

    // Loading state for initial and refresh data fetch
    @observable
    loading: boolean = false;

    // Unloading state to track when a model is being unloaded
    @observable
    unloading: boolean = false;

    // Configurable fetch interval time in milliseconds
    @observable
    fetchIntervalTime: number = defaultIntervalTime;

    // Interval reference for auto-refresh functionality
    fetchInterval: ReturnType<typeof setInterval> | null = null;

    constructor(private readonly service: GlobalAdministrationService) {
        // React to changes in fetchIntervalTime and restart the interval
        reaction(
            () => this.fetchIntervalTime,
            () => {
                if (this.fetchInterval) {
                    this.clearFetchInterval();
                    this.setupFetchInterval();
                }
            }
        );
    }

    // Initialize the store: fetch data and start auto-refresh
    @action.bound
    initialize() {
        this.loading = true;
        this.getLoadedModels().then(() => this.setupFetchInterval());
    }

    // Cleanup: reset state, clear interval, and dispose of reaction
    @action.bound
    dispose() {
        this.loading = false;
        this.loadedModels = [];
        this.fetchIntervalTime = defaultIntervalTime;
        this.clearFetchInterval();
    }

    // Set up auto-refresh interval
    @action.bound
    setupFetchInterval() {
        this.fetchInterval = setInterval(() => {
            this.getLoadedModels();
        }, this.fetchIntervalTime);
    }

    // Clear the auto-refresh interval
    @action.bound
    clearFetchInterval() {
        if (this.fetchInterval) {
            clearInterval(this.fetchInterval);
            this.fetchInterval = null;
        }
    }

    // Update the fetch interval time
    @action.bound
    setFetchIntervalTime(fetchIntervalTime: number) {
        this.fetchIntervalTime = fetchIntervalTime;
    }

    // Fetch loaded models from the backend
    @action.bound
    async getLoadedModels() {
        // Loading is not set to true here to avoid UI flickering when models are being refreshed in the interval
        try {
            const resp = await this.service.getLoadedModels();

            if (!resp.isOk()) {
                return;
            }

            // Update the observable list with fetched models
            resp.map(loadedModels => (this.loadedModels = loadedModels));
        } finally {
            this.loading = false;
        }
    }

    // Unload a specific ML model
    @action.bound
    async unloadModel(modelId: string, targetService: TargetMlModelService) {
        this.unloading = true;

        try {
            const resp = await this.service.unloadMlModel(modelId, targetService);

            if (resp.isOk()) {
                // Refresh the models list after successful unload
                await this.getLoadedModels();
            }
        } finally {
            this.unloading = false;
        }
    }
}
